import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import SearchDropdown, {
  Item,
} from "components/dropdowns/SearchDropdown/SearchDropdown";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  id?: string;
  type: string;
  handleData: (codes: string[]) => void;
  isReadOnly?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

const RestrictionTab: FC<Props> = ({
  id,
  type,
  handleData,
  isReadOnly = false,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const countriesList = useAppSelector((store) => store.settings.countries);
  const [selectedCountries, setSelectedCountries] = useState<Item[]>([]);
  const [isFetched, setIsFetched] = useState(false);

  const countryItems: Item[] =
    countriesList?.map((country) => ({
      value: country.code,
      label: country.name,
      icon: `https://flagpedia.net/data/flags/icon/16x12/${country.code.toLowerCase()}.png`,
    })) ?? [];

  useEffect(() => {
    onLoadingChange?.(!isFetched);
  }, [isFetched, onLoadingChange]);

  useEffect(() => {
    if (!countriesList?.length) return;

    if (!id) {
      setIsFetched(true);
      return;
    }

    const fetchRestrictions = async () => {
      setIsFetched(false);

      try {
        const result = await instance.get(
          `${apiRoutes.getRestrictionsCountriesList}/${id}/${type}`,
        );

        const codes: string[] = result?.data?.restrictedCountries ?? [];

        const defaults: Item[] =
          countriesList
            .filter((country) => codes.includes(country.code))
            .map((country) => ({
              value: country.code,
              label: country.name,
              icon: `https://flagpedia.net/data/flags/icon/16x12/${country.code.toLowerCase()}.png`,
            })) ?? [];

        setSelectedCountries(defaults);
        handleData(codes);
      } catch {
        console.log("fetchRestrictions error");
      }

      setIsFetched(true);
    };

    fetchRestrictions();
  }, [id, type, countriesList]);

  const handleSelect = (item: Item & { removed?: boolean }) => {
    if (isReadOnly) return;

    setSelectedCountries((prev) => {
      const updated = item.removed
        ? prev.filter((el) => el.value !== item.value)
        : prev.some((el) => el.value === item.value)
          ? prev
          : [...prev, item];

      handleData(updated.map((el) => el.value as string));
      return updated;
    });
  };

  return (
    <div className="game-form-wrapper">
      <div className="game-form-restriction">
        {isFetched ? (
          <>
            <div className="restriction-field">
              <div className="restriction-title geo">{t("geo")}</div>
              <SearchDropdown
                label={t("selectCountry")}
                items={countryItems}
                defaultValues={selectedCountries}
                onSelect={handleSelect}
                visibleItems={10}
                disabled={isReadOnly}
              />
            </div>
          </>
        ) : (
          <ModalTabSkeleton />
        )}
      </div>
    </div>
  );
};

export default RestrictionTab;
